import React, { useEffect, useRef, useState } from "react";
import { BsFillPauseFill, BsFillPlayFill, BsFullscreen, BsVolumeMuteFill, BsVolumeUpFill } from "react-icons/bs";
import { getMediaUrl } from "../../lib/utils";

const CommentVideo = ({ content }) => {
  const videoRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [muted, setMuted] = useState(false);

  const videoUrl = getMediaUrl(content);

  useEffect(() => {
    setIsPlaying(false);
    setHasStarted(false);
    setDuration(0);
    setCurrentTime(0);
    setMuted(false);
  }, [videoUrl]);

  if (!content) return null;

  const formatTime = (seconds) => {
    if (!Number.isFinite(seconds)) return "0:00";

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60).toString().padStart(2, "0");

    return `${minutes}:${remainingSeconds}`;
  };

  const togglePlay = async () => {
    const video = videoRef.current;

    if (!video) return;

    try {
      if (video.paused) {
        setHasStarted(true);
        await video.play();
      } else {
        video.pause();
      }
    } catch (error) {
      console.error("Comment video playback error:", error);
    }
  };

  const handleProgress = (event) => {
    const video = videoRef.current;

    if (!video || !Number.isFinite(video.duration)) return;

    const nextTime = Number(event.target.value);
    video.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  const toggleMute = () => {
    const video = videoRef.current;

    if (!video) return;

    const nextMuted = !video.muted;

    video.muted = nextMuted;
    setMuted(nextMuted);
  };

  const toggleFullscreen = async () => {
    const video = videoRef.current;

    if (!video) return;

    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await video.requestFullscreen();
      }
    } catch (error) {
      console.error("Fullscreen error:", error);
    }
  };

  return (
    <div className="mt-2 w-full max-w-[340px]">
      <div className="group relative aspect-video w-full overflow-hidden rounded-xl bg-black shadow-sm">
        <video
          ref={videoRef}
          src={videoUrl}
          preload="metadata"
          playsInline
          className="h-full w-full object-cover"
          onLoadedMetadata={(event) => setDuration(event.currentTarget.duration || 0)}
          onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime || 0)}
          onPlay={() => {
            setIsPlaying(true);
            setHasStarted(true);
          }}
          onPause={() => setIsPlaying(false)}
          onEnded={() => {
            setIsPlaying(false);
            setCurrentTime(0);
          }}
        />

        {/* INITIAL PLAY BUTTON */}

        {!hasStarted && (
          <>
            <button type="button" onClick={togglePlay} className="absolute inset-0 flex items-center justify-center bg-black/10 transition hover:bg-black/20" aria-label="Play video">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-black/65 text-white shadow-lg backdrop-blur-sm transition hover:scale-105">
                <BsFillPlayFill className="ml-0.5 h-7 w-7" />
              </span>
            </button>

            {duration > 0 && (
              <span className="absolute bottom-2 right-2 rounded-md bg-black/70 px-2 py-1 text-[11px] font-medium text-white">
                {formatTime(duration)}
              </span>
            )}
          </>
        )}

        {/* PLAYING CONTROLS */}

        {hasStarted && (
          <div className={`absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 via-black/35 to-transparent px-3 pb-2 pt-8 transition-opacity ${isPlaying ? "opacity-0 group-hover:opacity-100" : "opacity-100"}`}>
            <input type="range" min="0" max={duration || 0} step="0.01" value={Math.min(currentTime, duration || 0)} onChange={handleProgress} className="h-1 w-full cursor-pointer accent-orange-500" />

            <div className="mt-2 flex items-center gap-3 text-white">
              <button type="button" onClick={togglePlay} className="flex h-7 w-7 items-center justify-center" aria-label={isPlaying ? "Pause video" : "Play video"}>
                {isPlaying ? <BsFillPauseFill size={22} /> : <BsFillPlayFill size={22} />}
              </button>

              <button type="button" onClick={toggleMute} className="flex h-7 w-7 items-center justify-center" aria-label={muted ? "Unmute video" : "Mute video"}>
                {muted ? <BsVolumeMuteFill size={18} /> : <BsVolumeUpFill size={18} />}
              </button>

              <span className="text-[11px] font-medium">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>

              <button type="button" onClick={toggleFullscreen} className="ml-auto flex h-7 w-7 items-center justify-center" aria-label="Fullscreen">
                <BsFullscreen size={15} />
              </button>
            </div>
          </div>
        )}

        {/* PAUSED CENTER BUTTON */}

        {hasStarted && !isPlaying && (
          <button type="button" onClick={togglePlay} className="absolute left-1/2 top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white shadow-md backdrop-blur-sm" aria-label="Resume video">
            <BsFillPlayFill className="ml-0.5" size={25} />
          </button>
        )}
      </div>
    </div>
  );
};

export default CommentVideo;